import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import api from '../../services/api';
import Swal from 'sweetalert2';
import {
  Plus,
  Trash2
} from 'lucide-react';

function TcnGiftCardProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '',
    customer_ref: '',
    token_url: '',
    api_url: '',
    client_id: '',
    client_secret: '',
    promotion_id: '',
    product_id: ''
  });

  const fetchProfiles = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const { data } = await api.get('/admin/tcn-gift-card-profiles');
      setProfiles(Array.isArray(data?.profiles) ? data.profiles : []);
    } catch (err) {
      console.error('Error fetching TCN profiles:', err);
      setError('Failed to fetch TCN profiles');
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const filtered = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    if (!q) return profiles;
    return profiles.filter((p) => String(p?.name || '').toLowerCase().includes(q));
  }, [profiles, searchInput]);

  const handleDelete = async (profile) => {
    if (!profile?.id) return;
    const result = await Swal.fire({
      title: 'Delete profile?',
      text: `This will detach it from any campaigns using it.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/admin/tcn-gift-card-profiles/${profile.id}`);
      await Swal.fire('Deleted!', 'TCN profile deleted.', 'success');
      fetchProfiles();
    } catch (err) {
      console.error('Error deleting TCN profile:', err);
      await Swal.fire('Error', err.response?.data?.error || 'Failed to delete profile', 'error');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/admin/tcn-gift-card-profiles', form);
      await Swal.fire('Created!', 'TCN profile created.', 'success');
      setCreateOpen(false);
      setForm({
        name: '',
        customer_ref: '',
        token_url: '',
        api_url: '',
        client_id: '',
        client_secret: '',
        promotion_id: '',
        product_id: ''
      });
      fetchProfiles();
    } catch (err) {
      console.error('Error creating TCN profile:', err);
      await Swal.fire('Error', err.response?.data?.error || 'Failed to create profile', 'error');
    } finally {
      setCreating(false);
    }
  };

  return (
    <AdminLayout activeSection="CAMPAIGN">
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-white mb-6">TCN PROFILES</h1>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name…"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              New profile
            </button>
          </div>

          <div />
        </div>

        <div className="mt-4 text-sm text-gray-600">
          {profiles.length === 0 ? 'No profiles' : `Showing ${filtered.length} of ${profiles.length} profiles`}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden" style={{ minHeight: '600px' }}>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-teal-800 text-white">
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">NAME</th>
                <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(loading ? [] : filtered).map((p) => (
                <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{p.name}</div>
                    <div className="text-xs text-gray-500">ID: {p.id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <button
                      type="button"
                      onClick={() => handleDelete(p)}
                      className="text-red-600 hover:text-red-800 inline-flex items-center justify-center"
                      title="Delete profile"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!loading && filtered.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No profiles match your search.</p>
            </div>
          )}

          {loading && (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading…</p>
            </div>
          )}
        </div>
      </div>

      {createOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full mx-4 max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-start px-8 pt-8 pb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Create TCN Profile</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Client secret is encrypted server-side using <code className="font-mono">TCN_SETTINGS_ENCRYPTION_KEY</code>.
                </p>
              </div>
              <button
                onClick={() => !creating && setCreateOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-8 pb-8 overflow-y-auto flex-1">
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    ['name', 'Name', 'e.g. TCN Prod AU'],
                    ['customer_ref', 'Customer Ref', ''],
                    ['token_url', 'Token URL', ''],
                    ['api_url', 'API URL', ''],
                    ['client_id', 'Client ID', ''],
                    ['client_secret', 'Client Secret', ''],
                    ['promotion_id', 'Promotion ID', ''],
                    ['product_id', 'Product ID', '']
                  ].map(([key, label, placeholder]) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                      <input
                        type={key === 'client_secret' ? 'password' : 'text'}
                        value={form[key]}
                        onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
                        placeholder={placeholder}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        required
                      />
                    </div>
                  ))}
                </div>

                <div className="pt-4 flex justify-end gap-3 border-t border-gray-100">
                  <button
                    type="button"
                    disabled={creating}
                    onClick={() => setCreateOpen(false)}
                    className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50"
                  >
                    {creating ? 'Creating…' : 'Create profile'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default TcnGiftCardProfiles;

